import { useEffect, useState } from "react";
import { MASTERDATA_TYPE } from "../constants/master-data";
import { IMasterDataType } from "../interfaces/master-data";
import { ISuppliersByNameResponse, ISuppliersByNameResult } from "../interfaces/supplier";
import { BookingSupplierCostServices } from "../services/booking-supplier-cost";
import { ExpectingSupplierPaymentServices } from "../services/expect-supplier-payment";
import { MasterDataServices } from "../services/master-data";
import { SupplierServices } from "../services/supplier";
import { SupplierInvoiceRecordServices } from "../services/supplier-invoice-record";

interface DropdownsDataResult {
  suppliers?: ISuppliersByNameResult[];
  currencies?: IMasterDataType[];
  apOwners?: any[];
  sirStatuses?: string[];
  sirBookingNos?: string[];
  bscStatuses?: string[];
  bscParentBookingRefs?: string[];
  espStatuses?: string[];
}

const TYPE_TO_KEY_MAP = {
  SIR: {
    statuses: "sirStatuses",
    bookingNos: "sirBookingNos",
  },
  BSC: {
    statuses: "bscStatuses",
    parentBookingRefs: "bscParentBookingRefs",
  },
  ESP: {
    statuses: "espStatuses",
  },
} as const;

const DROPDOWN_TYPES = [
  { key: "SIR", types: ["statuses", "bookingNos"], filters: ["sirStatuses", "sirBookingNos"] },
  { key: "BSC", types: ["statuses", "parentBookingRefs"], filters: ["bscStatuses", "bscParentBookingRefs"] },
  { key: "ESP", types: ["statuses"], filters: ["espStatuses"] },
];

const fetchDropdownByType = {
  SIR: async (type: string): Promise<{ key: keyof DropdownsDataResult; data: any[] }> => {
    const res = await SupplierInvoiceRecordServices.getDropdownByType(type);
    return { key: (TYPE_TO_KEY_MAP.SIR as any)[type] as keyof DropdownsDataResult, data: res?.data || [] };
  },
  BSC: async (type: string): Promise<{ key: keyof DropdownsDataResult; data: any[] }> => {
    const res = await BookingSupplierCostServices.getDropdownByType(type);
    return { key: (TYPE_TO_KEY_MAP.BSC as any)[type] as keyof DropdownsDataResult, data: res?.data || [] };
  },
  ESP: async (type: string): Promise<{ key: keyof DropdownsDataResult; data: any[] }> => {
    const res = await ExpectingSupplierPaymentServices.getDropdownByType(type);
    return { key: (TYPE_TO_KEY_MAP.ESP as any)[type] as keyof DropdownsDataResult, data: res?.data || [] };
  },
};

async function fetchCurrencies(): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res = await MasterDataServices.getAllByType(MASTERDATA_TYPE.CURRENCY);
  return { key: "currencies", data: res?.data || [] };
}

async function fetchSuppliers(isExcludeReport?: boolean): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res: ISuppliersByNameResponse = await SupplierServices.getSuppliersByName({
    params: { page: 1, pageSize: 5000, isExcludeReport: isExcludeReport ? isExcludeReport : false },
  });
  return { key: "suppliers", data: res?.data?.results || [] };
}

async function fetchApOwners(): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res = await SupplierServices.getListUserBaseRole();
  return { key: "apOwners", data: res?.data.apOwner || [] };
}

export const useDropdownsReportData = (displayedColumnFilters: string[], enabled = true, isExcludeReport?: boolean) => {
  // Remove duplicates from displayedColumnFilters
  const uniqueFilters = Array.from(new Set(displayedColumnFilters));

  const [data, setData] = useState<DropdownsDataResult>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const reload = () => setReloadCounter((prev) => prev + 1);

  useEffect(() => {
    if (!enabled) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Only fetch dropdowns when related filters are present
        const promises: Promise<{ key: keyof DropdownsDataResult; data: any[] }>[] = [];

        DROPDOWN_TYPES.forEach(({ key, types, filters }) => {
          types.forEach((type, idx) => {
            if (uniqueFilters.includes(filters[idx])) {
              promises.push(fetchDropdownByType[key as "SIR" | "BSC" | "ESP"](type));
            }
          });
        });

        // Currencies
        if (uniqueFilters.some((filter) => filter.includes("Currencies"))) {
          promises.push(fetchCurrencies());
        }
        // Suppliers
        if (uniqueFilters.some((filter) => filter.includes("SupplierCodes"))) {
          promises.push(fetchSuppliers(isExcludeReport));
        }
        // AP Owners
        if (uniqueFilters.some((filter) => filter.includes("ApOwners"))) {
          promises.push(fetchApOwners());
        }

        const results = await Promise.all(promises);

        const resultObj = results.reduce((acc, curr) => {
          acc[curr.key] = curr.data;
          return acc;
        }, {} as DropdownsDataResult);

        setData(resultObj);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadCounter, uniqueFilters.join(","), enabled]);

  return { data, loading, error, reload };
};
